import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { ArrowLeft, Loader2, Trophy, Users, Target } from "lucide-react";
import ProfessionalStandardsPanel from "../components/instructor/ProfessionalStandardsPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { key: "national", label: "National" },
  { key: "area", label: "My area" },
  { key: "plan", label: "My plan" },
];

/**
 * StandardsDashboard — full private view of the Professional Standards profile.
 * Renders the panel + tier thresholds + the leaderboard for context.
 */
export default function StandardsDashboard() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [me, setMe] = useState(null);
  const [scope, setScope] = useState("national");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/standards/me`, { withCredentials: true });
        if (!cancelled) setMe(data);
      } catch {
        /* silent */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/standards/leaderboard?scope=${scope}&limit=20`, { withCredentials: true });
        if (!cancelled) setBoard(data);
      } catch {
        if (!cancelled) setBoard({ items: [], total: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-24">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="standards-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">Operating Standards</p>
            <p className="text-[11px] text-slate-400 truncate">Discipline made visible.</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        <ProfessionalStandardsPanel mode="private" showLink={false} />

        {/* Tier thresholds reference */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-4 h-4 text-violet-400" />
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">
              How tiers are earned
            </p>
          </div>
          <ul className="space-y-1.5">
            {Object.entries(me?.tier_thresholds || {}).map(([k, v]) => (
              <li key={k} data-testid={`tier-thresh-${k}`} className="flex items-start gap-2 text-[11px]">
                <span className="font-extrabold uppercase tracking-wider text-slate-300 w-16 shrink-0">{k}</span>
                <span className="text-slate-400">{v}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[10px] text-slate-500 leading-snug">
            Standards are evaluated continuously. If your discipline drops, your tier drops — there's no gaming this.
          </p>
        </div>

        {/* Leaderboard */}
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-400" />
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-amber-400">
                Standards leaderboard
              </p>
            </div>
            <Users className="w-3.5 h-3.5 text-slate-500" />
          </div>

          <div className="flex gap-1 mb-3">
            {SCOPES.map((s) => (
              <button
                key={s.key}
                data-testid={`leaderboard-scope-${s.key}`}
                onClick={() => setScope(s.key)}
                className={`flex-1 px-2 py-1.5 text-[11px] font-extrabold rounded-full ${
                  scope === s.key ? "bg-violet-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {board?.items?.length ? (
            <ul data-testid="leaderboard-list" className="space-y-1.5">
              {board.items.map((row) => (
                <LeaderRow key={`${row.rank}-${row.display_name}`} row={row} />
              ))}
            </ul>
          ) : (
            <p className="text-[12px] text-slate-500 text-center py-4">
              Not enough active instructors yet to populate this scope.
            </p>
          )}
          {board?.total > 0 && (
            <p className="mt-2 text-[10px] text-slate-500 text-center">
              {board.total} active instructor{board.total === 1 ? "" : "s"} · ranked by Professional Score
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function LeaderRow({ row }) {
  return (
    <li
      data-testid={`leader-row-${row.rank}`}
      className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${
        row.is_me
          ? "bg-violet-600/15 border-violet-500/40"
          : "bg-slate-800 border-slate-700"
      }`}
    >
      <span className="w-6 text-center text-[12px] font-extrabold text-slate-300 tabular-nums">
        {row.rank}
      </span>
      <span className="flex-1 min-w-0 text-[12px] font-bold truncate">
        {row.display_name}
        {row.is_me && (
          <span className="ml-1 text-[9px] font-extrabold uppercase tracking-wider text-violet-300">you</span>
        )}
      </span>
      <span className="text-[10px] font-extrabold text-slate-400 truncate">{row.tier}</span>
      <span className="text-[12px] font-extrabold text-white tabular-nums">{row.score}</span>
    </li>
  );
}
